'use client';

import React from 'react';
import { isDesktop } from '@benzinga/device-utils';
import useElementPosition, { Position } from './useElementPosition';

const NAVIGATION_HEADER_ID = 'navigation-header';
const AD_BANNER_IDS = ['hideRotatingBanner', 'hideStreamlineBanner'];

function isPinnedPosition(position: string): boolean {
  return position === 'sticky' || position === 'fixed';
}

export const useDetectStickyHeader = (): {
  entry: IntersectionObserverEntry | null;
  isBreakingNewsBannerVisible: boolean;
  isStickyNavbar: boolean;
  position: Position;
} => {
  const [isStickyNavbar, setIsStickyNavbar] = React.useState(false);
  const [isBreakingNewsBannerVisible, setIsBreakingNewsBannerVisible] = React.useState(false);
  const [entry, setEntry] = React.useState<IntersectionObserverEntry | null>(null);
  const position = useElementPosition(NAVIGATION_HEADER_ID, { throttleMs: 100 });

  React.useEffect(() => {
    const hasIOSupport = !!window.IntersectionObserver;
    if (!hasIOSupport) return;
    const isStreamlineBannerHidden = AD_BANNER_IDS.some(id => localStorage.getItem(id) === 'true');

    const observer = new IntersectionObserver(
      ([observerEntry]: IntersectionObserverEntry[]) => {
        const headerElement = observerEntry.target as HTMLElement;
        const measuredHeaderHeight = observerEntry.boundingClientRect.height;
        const defaultDesktopNavbarHeight = 132;
        const streamlineBannerHeight = 70;
        const desktopHeightWithStreamlineBanner = defaultDesktopNavbarHeight + streamlineBannerHeight;
        const fallbackNavbarHeight = isStreamlineBannerHidden
          ? defaultDesktopNavbarHeight
          : desktopHeightWithStreamlineBanner;
        const navbarHeight = isDesktop() ? measuredHeaderHeight || fallbackNavbarHeight : measuredHeaderHeight;
        const computedPosition = window.getComputedStyle(headerElement).position;
        const isSticky = isPinnedPosition(computedPosition) || window.scrollY >= navbarHeight;
        setIsStickyNavbar(isSticky);
        setEntry(observerEntry);
      },
      { root: null, threshold: [0, 1] },
    );
    const headerElement = document?.getElementById(NAVIGATION_HEADER_ID) as HTMLElement;
    if (headerElement) {
      observer.observe(headerElement);
      const computedPosition = window.getComputedStyle(headerElement).position;
      if (isPinnedPosition(computedPosition)) {
        setIsStickyNavbar(true);
      }
    }

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const breakingNewsBannerElement = document?.getElementById('breaking-news-banner') as HTMLElement;
    setIsBreakingNewsBannerVisible(!!breakingNewsBannerElement);
  }, []);

  return { entry, isBreakingNewsBannerVisible, isStickyNavbar, position };
};

export default useDetectStickyHeader;
